import { apiFetch } from "@/lib/api-client";
import type { BlogPost as DbBlogPost } from "@etfsite/db/types";
import { useQuery } from "@tanstack/react-query";

/**
 * Public blog posts — backed by `/api/data/posts` (Postgres `blog_posts`).
 *
 * The API already applies the public gate server-side: it returns ONLY posts
 * where `status === 'published'` AND the current env's `isLive{Env}` flag is
 * true. The web layer therefore does NO additional filtering — it just maps
 * the DB row shape into the view shape the blog pages render.
 *
 * The endpoint degrades to an empty list / 404 when the `blog_posts` table is
 * missing or a query fails, so these hooks never surface a 500 to the page.
 */

/** View-model shape the blog pages render. Normalizes the nullable DB row. */
export interface BlogPostView {
  slug: string;
  title: string;
  excerpt: string;
  category: string;
  author: string;
  authorInitials: string;
  authorTitle: string;
  publishedAt: string | null;
  readTimeMinutes: number;
  contentMarkdown: string;
  /** DDB fund slug for the "Related Fund" card (best-effort). */
  relatedFundSlug?: string;
  featured: boolean;
}

interface PostsListResponse {
  posts: DbBlogPost[];
}
interface PostResponse {
  post: DbBlogPost;
}

function toView(post: DbBlogPost): BlogPostView {
  return {
    slug: post.slug,
    title: post.title,
    excerpt: post.excerpt ?? "",
    category: post.category ?? "Insights",
    author: post.author ?? "Corgi Research",
    authorInitials: post.authorInitials ?? "CR",
    authorTitle: post.authorTitle ?? "",
    publishedAt:
      typeof post.publishedAt === "string"
        ? post.publishedAt
        : post.publishedAt
          ? new Date(post.publishedAt).toISOString()
          : null,
    readTimeMinutes: post.readTimeMinutes ?? 0,
    contentMarkdown: post.contentMarkdown ?? "",
    relatedFundSlug: post.fundId ?? undefined,
    featured: post.isFeatured,
  };
}

const STALE_TIME = 5 * 60 * 1000; // 5 min — posts change rarely.

/** All published + env-live posts, newest first (API-ordered). */
export function useBlogPosts() {
  return useQuery({
    queryKey: ["posts"],
    queryFn: () => apiFetch<PostsListResponse>("/api/data/posts").then((r) => r.posts.map(toView)),
    staleTime: STALE_TIME,
  });
}

/** Single published + env-live post by slug. 404 resolves to `null`. */
export function useBlogPost(slug: string | null | undefined) {
  return useQuery({
    queryKey: ["posts", "by-slug", slug],
    queryFn: () =>
      apiFetch<PostResponse>(`/api/data/posts/${slug}`)
        .then((r) => toView(r.post))
        .catch((error: unknown) => {
          // 404 = no such published/live post — render the not-found fallback,
          // don't bubble it as a query error.
          if (error instanceof Error && error.message.includes("404")) return null;
          throw error;
        }),
    enabled: Boolean(slug),
    staleTime: STALE_TIME,
    retry: (failureCount, error) => {
      if (error instanceof Error && error.message.includes("404")) return false;
      return failureCount < 1;
    },
  });
}
